import { useMemo } from "react";
import { useAccount } from "wagmi";
import type { Address } from "viem";

import type { VenueId } from "@/config/contracts";
import { CADC, USDC } from "@/config/tokens";
import {
  accruedBorrowAssets,
  accruedTotalBorrowAssets,
} from "@/lib/loop/morpho";
import {
  computeAjnaRisk,
  computeRisk,
  type RiskMetrics,
} from "@/lib/loop/risk";
import { toNumber } from "@/lib/units";

import { useAjnaBorrower } from "./use-ajna-borrower";
import { useAjnaPool } from "./use-ajna-pool";
import { useMorphoMarket } from "./use-morpho-market";
import { useMorphoPosition } from "./use-morpho-position";

export type PositionSummary = {
  readonly venue: VenueId;
  readonly loading: boolean;
  readonly hasPosition: boolean;
  readonly collateralRaw: bigint | null;
  readonly borrowRaw: bigint | null;
  /** Current effective leverage = exposure / equity. null if not derivable. */
  readonly leverage: number | null;
  readonly risk: RiskMetrics | null;
  /** Borrow cost APR (decimal). null until market data loads. */
  readonly borrowApr: number | null;
  /** Lend APY on the collateral leg. CADC earns no venue yield, so 0. */
  readonly lendApr: number;
  /** Combined net carry on equity (decimal). Negative = a cost. */
  readonly netApr: number | null;
  /** Ajna only: the deterministic sub-account (proxy) address. null for Morpho. */
  readonly ajnaPositionAddress: Address | null;
};

/**
 * Read-only overview of a wallet's open position on one venue, packaged for the
 * Positions page. Independent of the builder forms so it can be rendered for
 * every venue at once — react-query dedupes the underlying market reads.
 *
 * Both venues' market/pool reads fire regardless of `venue` so the hook obeys
 * the rules-of-hooks; only the matching venue's data is used.
 */
export const usePositionSummary = (venue: VenueId): PositionSummary => {
  const { address: account } = useAccount();
  const isMorpho = venue === "morpho";

  const marketQuery = useMorphoMarket();
  const market = isMorpho ? marketQuery.data ?? null : null;
  const positionQuery = useMorphoPosition(isMorpho ? account : undefined);
  const position = isMorpho ? positionQuery.data ?? null : null;

  const poolQuery = useAjnaPool();
  const pool = !isMorpho ? poolQuery.data ?? null : null;
  const borrowerQuery = useAjnaBorrower(!isMorpho ? account : undefined);
  const borrower = !isMorpho ? borrowerQuery.data ?? null : null;

  // Off-chain interest accrual anchored to the market fetch time, matching the
  // convention used by the builder forms.
  const nowSec = BigInt(Math.floor(marketQuery.dataUpdatedAt / 1000));
  const morphoBorrow = useMemo<bigint>(
    () =>
      market && position
        ? accruedBorrowAssets(
            position.borrowShares,
            market.state.totalBorrowShares,
            accruedTotalBorrowAssets(
              market.state,
              market.borrowRatePerSec,
              nowSec,
            ),
          )
        : 0n,
    [market, position, nowSec],
  );

  const ajnaDebt = useMemo<bigint>(() => {
    if (!pool || !borrower || borrower.t0debt === 0n) return 0n;
    const wad = (borrower.t0debt * pool.inflator) / 10n ** 18n;
    return wad / 10n ** 12n;
  }, [pool, borrower]);

  // USDC per 1 CADC from the venue's own oracle.
  const price = useMemo<number>(() => {
    if (isMorpho) return market ? Number(market.oraclePrice) / 1e24 : 0;
    return pool ? Number(pool.lup) / 1e18 : 0;
  }, [isMorpho, market, pool]);

  const collateralRaw = isMorpho
    ? position?.collateral ?? null
    : borrower?.collateral ?? null;
  const borrowRaw = isMorpho
    ? position
      ? morphoBorrow
      : null
    : borrower
      ? ajnaDebt
      : null;

  const risk = useMemo<RiskMetrics | null>(() => {
    if (isMorpho) {
      if (!market || !position) return null;
      return computeRisk({
        collateralRaw: position.collateral,
        borrowRaw: morphoBorrow,
        oraclePrice: market.oraclePrice,
        lltv: market.params.lltv,
        loanDecimals: USDC.decimals,
        collateralDecimals: CADC.decimals,
      });
    }
    if (!pool || !borrower) return null;
    return computeAjnaRisk({
      collateralRaw: borrower.collateral,
      debtUsdcRaw: ajnaDebt,
      lup: pool.lup,
    });
  }, [isMorpho, market, position, morphoBorrow, pool, borrower, ajnaDebt]);

  const cHuman = collateralRaw == null ? 0 : toNumber(collateralRaw, CADC.decimals);
  const dHuman = borrowRaw == null ? 0 : toNumber(borrowRaw, USDC.decimals);
  const leverage = useMemo<number | null>(() => {
    if (collateralRaw == null) return null;
    if (cHuman === 0 || price === 0) return 1;
    const exposure = cHuman * price;
    const equity = exposure - dHuman;
    if (equity <= 0) return null;
    return exposure / equity;
  }, [collateralRaw, cHuman, dHuman, price]);

  const borrowApr = isMorpho
    ? market?.borrowApr ?? null
    : pool?.borrowApr ?? null;
  const lendApr = 0;
  const netApr = useMemo<number | null>(() => {
    if (borrowApr == null || leverage == null) return null;
    return lendApr * leverage - borrowApr * Math.max(0, leverage - 1);
  }, [borrowApr, leverage]);

  const hasPosition =
    (collateralRaw != null && collateralRaw > 0n) ||
    (borrowRaw != null && borrowRaw > 0n);

  const loading =
    account != null &&
    (isMorpho
      ? marketQuery.isLoading || positionQuery.isLoading
      : poolQuery.isLoading || borrowerQuery.isLoading);

  return {
    venue,
    loading,
    hasPosition,
    collateralRaw,
    borrowRaw,
    leverage,
    risk,
    borrowApr,
    lendApr,
    netApr,
    ajnaPositionAddress: !isMorpho ? (borrower?.positionAddress ?? null) : null,
  };
};
